import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface OnboardingState {
  onboardingCompleted: boolean;
  firstProjectTutorialCompleted: boolean;
  loading: boolean;
}

export function useOnboarding() {
  const { user } = useAuth();
  const [state, setState] = useState<OnboardingState>({
    onboardingCompleted: false,
    firstProjectTutorialCompleted: false,
    loading: true,
  });

  // Local state for tutorial progress (task completion)
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);

  // Fetch onboarding state from profiles
  useEffect(() => {
    async function fetchOnboardingState() {
      if (!user) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed, first_project_tutorial_completed")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error fetching onboarding state:", error);
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        setState({
          onboardingCompleted: data?.onboarding_completed ?? false,
          firstProjectTutorialCompleted: data?.first_project_tutorial_completed ?? false,
          loading: false,
        });
      } catch (err) {
        console.error("Error fetching onboarding state:", err);
        setState((prev) => ({ ...prev, loading: false }));
      }
    }

    fetchOnboardingState();
  }, [user]);

  // Mark onboarding as completed
  const completeOnboarding = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", user.id);

      if (error) {
        console.error("Error updating onboarding state:", error);
        return;
      }

      setState((prev) => ({ ...prev, onboardingCompleted: true }));
    } catch (err) {
      console.error("Error updating onboarding state:", err);
    }
  }, [user]);

  // Mark first project tutorial as completed
  const completeFirstProjectTutorial = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ first_project_tutorial_completed: true })
        .eq("id", user.id);

      if (error) {
        console.error("Error updating tutorial state:", error);
        return;
      }

      setState((prev) => ({ ...prev, firstProjectTutorialCompleted: true }));
    } catch (err) {
      console.error("Error updating tutorial state:", err);
    }
  }, [user]);

  // Reset onboarding (for re-opening from help)
  const resetOnboarding = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          onboarding_completed: false,
          first_project_tutorial_completed: false,
        })
        .eq("id", user.id);

      if (error) {
        console.error("Error resetting onboarding:", error);
        return;
      }

      setState({
        onboardingCompleted: false,
        firstProjectTutorialCompleted: false,
        loading: false,
      });
      setCompletedTasks([]);
    } catch (err) {
      console.error("Error resetting onboarding:", err);
    }
  }, [user]);

  // Complete a tutorial task
  const completeTask = useCallback((taskId: string) => {
    setCompletedTasks((prev) => {
      if (prev.includes(taskId)) return prev;
      return [...prev, taskId];
    });
  }, []);

  return {
    ...state,
    completedTasks,
    completeOnboarding,
    completeFirstProjectTutorial,
    resetOnboarding,
    completeTask,
  };
}
